import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, UserCircle2, Sparkles, Headphones } from "lucide-react";
import { api } from "@/lib/api";

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [mode, setMode] = useState("ai");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [agentOnline, setAgentOnline] = useState(false);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const ensureSession = async () => {
    if (session) return session;
    const name = localStorage.getItem("kk_chat_name") || "Guest";
    const { data } = await api.post("/chat/start", { name });
    setSession(data);
    localStorage.setItem("kk_chat_sid", data.id);
    setMessages([
      {
        role: "ai",
        text: "Hello — I'm Konoka, your jewellery concierge. Ask me about sizing, materials, gifting, or shipping. ✨",
      },
    ]);
    return data;
  };

  useEffect(() => {
    if (!open) return;
    if (!session) ensureSession();
  }, [open]); // eslint-disable-line

  // Poll support availability whenever the widget is open
  useEffect(() => {
    if (!open) return;
    const check = () =>
      api
        .get("/support/availability")
        .then((r) => setAgentOnline(!!r.data?.available))
        .catch(() => {});
    check();
    const t = setInterval(check, 20000);
    return () => clearInterval(t);
  }, [open]);

  // Poll for new messages while in live mode
  useEffect(() => {
    if (!session || mode !== "live") return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/chat/${session.id}`);
        setMessages(data.messages || []);
        if (data.mode !== mode) setMode(data.mode);
      } catch {}
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [session, mode]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    const sess = await ensureSession();
    setSending(true);
    const optimistic = [...messages, { role: "user", text: t }];
    setMessages(optimistic);
    setText("");
    try {
      if (mode === "ai") {
        const { data } = await api.post(`/chat/${sess.id}/message`, { text: t });
        if (data.reply) {
          setMessages((m) => [...m, { role: "ai", text: data.reply }]);
        }
      } else {
        await api.post(`/chat/${sess.id}/message`, { text: t });
      }
    } catch {
      setMessages((m) => [
        ...m,
        { role: "ai", text: "Something went wrong. Please try again or tap Talk to a human." },
      ]);
    } finally {
      setSending(false);
    }
  };

  const talkToHuman = async () => {
    const sess = await ensureSession();
    // Check support availability first
    let available = true;
    try {
      const { data } = await api.get("/support/availability");
      available = !!data.available;
    } catch {}
    await api.post(`/chat/${sess.id}/request-human`);
    setMode("live");
    if (available) {
      setMessages((m) => [
        ...m,
        {
          role: "system",
          text: "Connecting you to one of our stylists. They will join in a moment.",
        },
      ]);
    } else {
      setMessages((m) => [
        ...m,
        {
          role: "system",
          text:
            "No stylist is online right now — leave a note with your concern and the best way to reach you, and we'll get back to you within a few hours.",
        },
      ]);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-[#1a1918] text-white w-14 h-14 rounded-full shadow-xl flex items-center justify-center hover:bg-[#c58b76] transition-colors"
          data-testid="chat-toggle"
          aria-label="Open chat"
        >
          <MessageCircle size={22} />
        </button>
      )}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[80vh] bg-white border border-[#ebe5df] shadow-2xl flex flex-col pop-in"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-[#ebe5df] flex items-center justify-between bg-[#1a1918] text-white">
            <div className="flex items-center gap-2">
              {mode === "ai" ? <Sparkles size={16} className="text-[#c58b76]" /> : <Headphones size={16} className="text-[#c58b76]" />}
              <div>
                <div className="font-serif text-lg leading-none">{mode === "ai" ? "Konoka AI" : "Live Stylist"}</div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-[#a8a39d] flex items-center gap-1.5">
                  <span
                    className={`inline-block w-1.5 h-1.5 rounded-full ${
                      mode === "ai" ? "bg-[#c58b76]" : agentOnline ? "bg-[#69c08a]" : "bg-[#8b3a36]"
                    }`}
                    data-testid="agent-status-dot"
                  ></span>
                  {mode === "ai"
                    ? "Always available"
                    : agentOnline
                    ? "Stylists online"
                    : "No stylist online"}
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close" aria-label="Close chat">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#fdfbf7]">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] px-3 py-2 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#1a1918] text-white"
                      : m.role === "system"
                      ? "bg-transparent text-[#5c5a56] italic text-xs text-center w-full"
                      : m.role === "agent"
                      ? "bg-[#f3efea] border border-[#c58b76] text-[#1a1918]"
                      : "bg-[#f3efea] text-[#1a1918]"
                  }`}
                  data-testid={`chat-message-${i}`}
                >
                  {m.role === "agent" && (
                    <div className="overline text-[#c58b76] mb-1">{m.agent_name || "Stylist"}</div>
                  )}
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="text-xs text-[#5c5a56] italic">Konoka is thinking…</div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-[#ebe5df] bg-white">
            {mode === "ai" && (
              <button
                onClick={talkToHuman}
                className="w-full text-xs tracking-[0.16em] uppercase text-[#c58b76] hover:text-[#1a1918] transition-colors mb-2 flex items-center justify-center gap-1.5"
                data-testid="talk-to-human"
              >
                <UserCircle2 size={14} />
                {agentOnline ? "Talk to a human" : "No stylist online · leave a message"}
              </button>
            )}
            <div className="flex items-center gap-2">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask anything…"
                className="flex-1 border border-[#ebe5df] px-3 py-2 text-sm focus:outline-none focus:border-[#c58b76]"
                data-testid="chat-input"
              />
              <button
                onClick={send}
                className="bg-[#1a1918] text-white w-10 h-10 flex items-center justify-center hover:bg-[#c58b76] transition-colors"
                data-testid="chat-send"
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
